import { CommonModule } from '@angular/common'
import { NgModule } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { UpgradePromptComponent } from '@employer/app/components/upgrade-prompt/upgrade-prompt.component'
import { MessageModule } from '@engineering11/messaging-web'
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button'
import { E11CardModule } from '@engineering11/ui-lib/e11-card'
import { E11EmptyStateComponent } from '@engineering11/ui-lib/e11-empty-state'
import { E11LoaderBouncingDotsComponent } from '@engineering11/ui-lib/e11-loader-bouncing-dots'
import { E11TagModule } from '@engineering11/ui-lib/e11-tag'
import { LetDirective } from '@ngrx/component'
import { TranslateModule } from '@ngx-translate/core'
import { AssistantBarActiveComponent } from './components/assistant-bar-active/assistant-bar-active.component'
import { AssistantChatPanelComponent } from './components/assistant-chat-panel/assistant-chat-panel.component'
import { AssistantConfirmationCardComponent } from './components/assistant-confirmation-card/assistant-confirmation-card.component'
import { AssistantHighlightOverlayComponent } from './components/assistant-highlight-overlay/assistant-highlight-overlay.component'
import { AssistantOnboardingOfferComponent } from './components/assistant-onboarding-offer/assistant-onboarding-offer.component'
import { AssistantTerminalComponent } from './components/assistant-terminal/assistant-terminal.component'
import { TourCardComponent } from './components/tour-card/tour-card.component'
import { TourIndicatorComponent } from './components/tour-indicator/tour-indicator.component'
import { TourOverlayComponent } from './components/tour-overlay/tour-overlay.component'
import { AssistantPostureService } from './services/assistant-posture.service'

/**
 * Assistant feature module. The assistant is the resizable terminal panel (`<assistant-terminal>`): the
 * conversation stream + its composer + the confirmation card, plus the floating guided-walkthrough card
 * (`<tour-card>`) with its anchored indicator layer (`<tour-indicator>`), the client-tool highlight
 * overlay, and the onboarding offer card. Exports the surfaces `main-layout` mounts (via SharedModule):
 * `<assistant-terminal>`, `<tour-card>`, `<tour-indicator>`, `<assistant-highlight-overlay>`,
 * `<assistant-onboarding-offer>`. The retired spotlight
 * `<tour-overlay>` stays declared/exported for its own spec but is no longer mounted.
 */
@NgModule({
  declarations: [
    AssistantTerminalComponent,
    AssistantBarActiveComponent,
    AssistantChatPanelComponent,
    AssistantConfirmationCardComponent,
    TourOverlayComponent,
    TourCardComponent,
    TourIndicatorComponent,
    AssistantHighlightOverlayComponent,
    AssistantOnboardingOfferComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    LetDirective,
    MessageModule, // exports <ui-message-markdown>
    E11ButtonModule,
    E11CardModule, // story 4.4 — <e11-card> narration callout in the tour overlay
    E11TagModule,
    E11EmptyStateComponent, // standalone
    E11LoaderBouncingDotsComponent, // standalone
    UpgradePromptComponent, // standalone — resolves <upgrade-prompt> in the terminal's upsell branch (story 4.3)
  ],
  exports: [
    AssistantTerminalComponent,
    TourOverlayComponent,
    TourCardComponent,
    TourIndicatorComponent,
    AssistantHighlightOverlayComponent,
    AssistantOnboardingOfferComponent,
  ],
})
export class AssistantModule {
  /**
   * Inject `AssistantPostureService` to force its eager instantiation on module load (SharedModule
   * imports this module app-wide), so its `tourTerminated$` subscription is live before any tour runs
   * and the guide→helper completion flip fires. The guide/helper UI toggle was removed with the rail;
   * the lifecycle flip stays until the tour is reworked (#4).
   */
  constructor(_posture: AssistantPostureService) {}
}
